import React from "react";
import { useApi } from "@tiny/api";
import { CustomSegmentSummaryResponseView } from "@tiny/api";
import {
  usePutCampaignFindFilterBasicQuery,
  usePutSegmentCustomSummaryQuery,
  usePutWorkflowsFindFilterQuery,
} from "@tiny/api";
import { useMessages } from "@tiny/components";
import { removeEmptyConditions } from "../../utils/segmentConditionUtils";
import {
  extractBackendError,
  getErrorMessage,
} from "@tiny/components/src/utils/errorUtils";
import { SegmentSummaryProps } from "./SegmentSummary";
import {
  extractAllCampaignsIds,
  extractAllWorkflowIds,
  filterToDescription,
} from "./SegmentSummary.helpers";

export function useSegmentSummary(props: SegmentSummaryProps) {
  const api = useApi();
  const {
    hideRefresh,
    noBorderRadius,
    onClose,
    refreshDisabled,
    refreshDisabledTooltip = "Please edit conditions before refresh.",
    isFilterValid = true,
    segmentId,
    onRefreshClick,
    skipInitialSummaryFetch = false,
  } = props;

  const filter = removeEmptyConditions(props.filter);

  const [loaded, setLoaded] = React.useState(false);

  const { showErrorMessage } = useMessages();

  const [refreshing, setRefreshing] = React.useState(false);

  const [summary, setSummary] =
    React.useState<CustomSegmentSummaryResponseView>(
      {} as CustomSegmentSummaryResponseView
    );

  const { isLoading } = usePutSegmentCustomSummaryQuery(
    {
      customSegmentId: segmentId,
      condition: filter,
    },
    {
      enabled: !skipInitialSummaryFetch && !loaded && !!filter,
      onSuccess(data) {
        setSummary(data);
        setLoaded(true);
      },
    }
  );

  const campaignsIds = React.useMemo(() => {
    return extractAllCampaignsIds(filter);
  }, [filter]);

  const workflowIds = React.useMemo(() => {
    return extractAllWorkflowIds(filter);
  }, [filter]);

  const {
    data: { content: campaigns = [] } = {},
    isLoading: isCampaignLoading,
  } = usePutCampaignFindFilterBasicQuery(
    {
      ids: campaignsIds,
    },
    {},
    {
      enabled: campaignsIds.length > 0,
    }
  );

  const {
    data: { content: workflows = [] } = {},
    isLoading: isWorkflowLoading,
  } = usePutWorkflowsFindFilterQuery(
    {
      ids: workflowIds,
    },
    {},
    {
      enabled: workflowIds.length > 0,
    }
  );

  const refresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      const resp = await api.putSegmentCustomSummary({
        condition: filter,
      });
      setSummary(resp.data);
      onRefreshClick?.();
    } catch (e: any) {
      extractBackendError(e.response).then((be) =>
        showErrorMessage(getErrorMessage(be))
      );
    } finally {
      setRefreshing(false);
    }
  }, [filter, showErrorMessage]);

  const description = React.useMemo(() => {
    return isFilterValid
      ? filterToDescription(props.filter, [...campaigns, ...workflows])
      : "None";
  }, [campaigns, workflows, isFilterValid, props.filter]);

  const {
    countCustomers = 0,
    countNetCustomers = 0,
    countDelivered = 0,
    countCustomersHavingOpened = 0,
    countCustomersHavingClicked = 0,
    countCustomersHavingUnsubscribed = 0,
    openedPercentage = 0,
    clickedPercentage = 0,
    unsubscribedPercentage = 0,
    audiences = [],
  } = summary;

  const calculatePercentage = (part?: number, total?: number) => {
    if (part === 0) {
      return 0;
    }
    return !!total && part !== undefined ? (100 * part) / total : 0;
  };

  const audiencesIn =
    audiences.filter((aud) => aud.criteria === "IN")?.[0]?.audiences || [];
  const audiencesNotIn =
    audiences.filter((aud) => aud.criteria === "NOT_IN")?.[0]?.audiences || [];

  return {
    audiencesIn,
    audiencesNotIn,
    refresh,
    countCustomers: isFilterValid ? countCustomers : 0,
    countNetCustomers: isFilterValid ? countNetCustomers : 0,
    clickedPercentage: isFilterValid ? clickedPercentage : 0,
    openedPercentage: isFilterValid ? openedPercentage : 0,
    isCampaignLoading,
    isWorkflowLoading,
    isLoading: isLoading || isWorkflowLoading,
    unsubscribedPercentage: isFilterValid ? unsubscribedPercentage : 0,
    refreshing,
    hideRefresh,
    description,
    noBorderRadius,
    onClose,
    refreshDisabled,
    refreshDisabledTooltip,
    isFilterValid,
    isps: summary?.isps,
  };
}
